import { Link, useNavigate } from "react-router-dom"
import {
  AuthPhoneBackdrop,
  AuthWarmBackdrop,
  authButton,
} from "../Components/AuthLayout"
import dealMasterLogo from "@/images/backgrounds/logo-without-dice.png"

// The address players are told to write to. Kept here rather than inline
// because it appears in three places below and is the one thing in this file
// that has to change when support moves.
const CONTACT_EMAIL = "privacy@dealmaster.game"

// The date shown at the top. Bump it whenever the text below changes in a way
// that affects what is collected or who it is shared with.
const LAST_UPDATED = "25 September 2026"

const bodyText =
  "font-third text-xl leading-relaxed text-brown-800 mobile:text-3xl mobile:text-primary/80"

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section className="mb-10 sm:mb-6">
    <h2 className="mb-3 font-third text-2xl font-semibold text-brown-700 mobile:text-4xl mobile:text-amber-300 sm:mb-2">
      {title}
    </h2>
    <div className={`flex flex-col gap-3 ${bodyText}`}>{children}</div>
  </section>
)

const Bullets: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul className="ml-6 flex list-disc flex-col gap-2 marker:text-red-500 mobile:marker:text-amber-300">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
)

interface PrivacyPolicyProps {}

const PrivacyPolicy: React.FC<PrivacyPolicyProps> = () => {
  const nav = useNavigate()

  // Reached from the login and sign-up screens, but also linkable on its own
  // (app stores ask for a direct URL), so a deep-linked visit has nowhere to go
  // back to and lands on login instead of leaving the app.
  const goBack = () => {
    if (window.history.length > 1) {
      nav(-1)
    } else {
      nav("/login")
    }
  }

  return (
    <div className="relative h-full w-full overflow-y-auto bg-primary px-8 py-12 sm:py-6 mobile:bg-transparent mobile:pb-[max(16px,var(--safe-bottom))] mobile:pl-[max(20px,var(--safe-left))] mobile:pr-[max(20px,var(--safe-right))] mobile:pt-[max(16px,var(--safe-top))]">
      {/* `fixed` rather than `absolute`: this column is the scroller, and an
          absolutely positioned backdrop inside it only covers the first
          screenful, leaving the rest of a long document on bare white. */}
      <div className="pointer-events-none fixed inset-0">
        <AuthPhoneBackdrop />
        <AuthWarmBackdrop />
      </div>

      <div className="relative mx-auto w-full max-w-[900px] mobile:rounded-[28px] mobile:border mobile:border-white/20 mobile:bg-black/60 mobile:px-6 mobile:py-6 mobile:shadow-[0_24px_70px_rgba(0,0,0,0.65)] mobile:backdrop-blur-xl">
        <img
          src={dealMasterLogo}
          className="mx-auto mb-6 hidden w-full max-w-[220px] drop-shadow-[0_8px_28px_rgba(0,0,0,0.8)] mobile:block sm:!mb-3 sm:!max-w-[140px]"
          alt="Deal Master"
        />

        <div className="form-heading mb-2 text-center uppercase mobile:!text-primary sm:mb-1 sm:!text-5xl">
          Privacy Policy
        </div>
        <div className="mb-10 text-center font-third text-base text-gray-500 mobile:text-3xl mobile:text-primary/70 sm:mb-6">
          Last updated {LAST_UPDATED}
        </div>

        <Section title="1. Who this policy covers">
          <p>
            This policy explains what Deal Master collects when you create an
            account, play a game, or simply open the app, what we do with it,
            and the choices you have. It applies to the Deal Master website and
            to the mobile and desktop builds of the game.
          </p>
          <p>
            By creating an account or playing as a guest, you agree to the
            handling of information described here.
          </p>
        </Section>

        <Section title="2. Information we collect">
          <p>
            <span className="font-semibold">Account details.</span> When you
            sign up we store your username, your email address, and your
            password. Passwords are stored only as a salted hash — we never keep
            the password itself, and nobody at Deal Master can read it.
          </p>
          <p>
            <span className="font-semibold">Sign-in with Google.</span> If you
            choose to continue with Google, we receive the account identifier
            and email address that Google returns for you, and we store the
            identifier so we can recognise you on your next visit. We never
            receive your Google password.
          </p>
          <p>
            <span className="font-semibold">Sign-in through Steam.</span> In the
            Steam build, Steam gives us your Steam ID and display name so the
            game can seat you at a table.
          </p>
          <p>
            <span className="font-semibold">Playing as a guest.</span> A guest
            session stores only the display name you type. It carries no email
            address and no password, and it is not linked to any account.
          </p>
          <p>
            <span className="font-semibold">Profile picture.</span> You can pick
            one of our built-in avatars or upload your own image. An uploaded
            image is stored on our servers and shown to the other players at
            your table.
          </p>
          <p>
            <span className="font-semibold">Gameplay and statistics.</span> We
            record the outcome of finished games so the stats, history and
            challenge screens can work: games played and won, ratings,
            properties bought, trades made, auctions won, dice rolled, and
            similar counters.
          </p>
          <p>
            <span className="font-semibold">Social features.</span> Your friends
            list, the invitations you send or receive, and the messages you send
            in a game's chat.
          </p>
          <p>
            <span className="font-semibold">Verification codes.</span> When you
            register or reset your password we generate a one-time code, email
            it to you, and store it in hashed form until it expires.
          </p>
          <p>
            <span className="font-semibold">Technical information.</span> Our
            servers keep ordinary connection logs — IP address, approximate
            time, and the type of device or browser — which we use to keep the
            game running and to investigate abuse.
          </p>
        </Section>

        <Section title="3. How we use your information">
          <Bullets
            items={[
              "To create and secure your account, and to sign you back in.",
              "To run multiplayer games: seating players, matching you with opponents, and showing your name and avatar to the other players at your table.",
              "To keep your progress, statistics, history and challenges.",
              "To send you the emails the game needs to work — verification codes and password resets.",
              "To detect cheating, abuse and automated accounts, and to keep the service stable.",
              "To answer you when you contact support.",
            ]}
          />
          <p>
            We do not sell your personal information, and we do not use it to
            build advertising profiles.
          </p>
        </Section>

        <Section title="4. What other players can see">
          <p>
            Other players in a game can see your username, your profile picture,
            your in-game actions, and anything you type into the game chat. Your
            email address is never shown to other players. Treat the chat as
            public, and please do not share personal details there.
          </p>
        </Section>

        <Section title="5. Who we share it with">
          <p>
            We share information only with the services that make the game work,
            and only with what each of them needs:
          </p>
          <Bullets
            items={[
              "Our hosting and database providers, which store the data described above.",
              "Google, if you choose to sign in with Google, under Google's own privacy policy.",
              "Steam, in the Steam build, under Valve's own privacy policy.",
              "Our email provider, which delivers verification and password-reset messages.",
            ]}
          />
          <p>
            We may also disclose information if the law requires it, or where it
            is necessary to protect the rights and safety of our players.
          </p>
        </Section>

        <Section title="6. Cookies and local storage">
          <p>
            Deal Master stores a small amount of data in your browser or app so
            that you stay signed in between visits, and so that settings such as
            sound, music and your preferred game piece survive a reload. This
            data stays on your device. We do not use advertising or
            third-party tracking cookies.
          </p>
        </Section>

        <Section title="7. How long we keep it">
          <p>
            We keep your account details and game history for as long as your
            account exists. Verification codes are discarded once they are used
            or expire. Connection logs are kept for a short period and then
            deleted. When you ask us to delete your account, we remove your
            account details and profile picture; finished games may remain in
            other players' history in a form that no longer identifies you.
          </p>
        </Section>

        <Section title="8. Security">
          <p>
            Passwords are hashed, traffic between the game and our servers is
            encrypted, and access to the database is restricted. No service can
            promise perfect security, so please use a password you do not reuse
            elsewhere, and tell us straight away if you think someone else has
            got into your account.
          </p>
        </Section>

        <Section title="9. Your choices and rights">
          <p>
            You can change your username, email address and profile picture at
            any time from the profile settings screen. Depending on where you
            live, you may also have the right to ask for a copy of the
            information we hold about you, to have it corrected, or to have it
            deleted. Write to us at{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="font-semibold text-red-500 underline underline-offset-4 mobile:text-amber-300"
            >
              {CONTACT_EMAIL}
            </a>{" "}
            and we will respond within a reasonable time.
          </p>
          <p>
            To delete your account outright, see{" "}
            <Link
              to="/delete-account"
              className="font-semibold text-red-500 underline underline-offset-4 mobile:text-amber-300"
            >
              how to delete your account
            </Link>
            .
          </p>
        </Section>

        <Section title="10. Children">
          <p>
            Deal Master is not directed at children under 13, and we do not
            knowingly collect their information. If you believe a child has
            created an account, contact us at {CONTACT_EMAIL} and we will remove
            it.
          </p>
        </Section>

        <Section title="11. Changes to this policy">
          <p>
            If we change how we handle your information, we will update this
            page and move the date at the top. Significant changes will be
            announced in the game before they take effect.
          </p>
        </Section>

        <Section title="12. Contact us">
          <p>
            Questions about this policy, or about the information we hold, go
            to{" "}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="font-semibold text-red-500 underline underline-offset-4 mobile:text-amber-300"
            >
              {CONTACT_EMAIL}
            </a>
            .
          </p>
        </Section>

        <div className="mt-12 flex flex-col items-center gap-4 border-t border-brown-300 pt-8 mobile:border-white/20 sm:mt-6 sm:pt-4">
          <button
            onClick={goBack}
            data-testid="privacy-policy-back-btn"
            className={`${authButton} max-w-[320px] border-2 border-brown-700 bg-transparent text-brown-700 hover:-translate-y-0.5 hover:bg-brown-700 hover:text-primary mobile:max-w-none mobile:border-amber-400/60 mobile:text-amber-200 mobile:hover:bg-amber-400 mobile:hover:text-black`}
          >
            Go back
          </button>
          <Link
            to="/login"
            className="font-third text-base text-gray-500 underline-offset-4 transition-colors hover:text-brown-700 hover:underline mobile:text-3xl mobile:text-primary/70 mobile:hover:text-amber-300"
          >
            Back to login
          </Link>
        </div>
      </div>
    </div>
  )
}

export default PrivacyPolicy
